/**
 * JINSA – Marcas en la búsqueda predictiva
 *
 *  - La predicción arranca desde 2 caracteres (antes 3).
 *  - Arriba de los resultados muestra las MARCAS que coinciden con lo que se va escribiendo
 *    (instantáneo, sin esperar a Shopify): "au" → Autonics, Auburn…
 *  - La marca principal siempre va antes que sus subcolecciones; éstas salen como botones
 *    debajo de la marca (Autonics → Switches, Encoders, Temporizadores…).
 *  - En la pestaña "Colecciones" se reordena: marcas principales, luego categorías, luego subcolecciones.
 *
 * Los datos salen de sections/jinsa-brands.liquid (se piden una vez cada 12 h y se guardan en el navegador).
 * No modifica predictive-search.js: se engancha al componente <predictive-search> cuando ya existe.
 */
(function () {
  'use strict';

  var CACHE_KEY = 'jinsa_brands_v2';
  var CACHE_TTL = 12 * 60 * 60 * 1000;
  var MIN_CHARS = 2;
  var MAX_CARDS = 3;
  var MAX_CHIPS = 8;
  var SECTION_URL = '/?section_id=jinsa-brands';

  var brands = null;      // lista ya preparada
  var loading = null;     // promesa de carga en curso

  /* ───────── utilidades ───────── */
  function norm(s) {
    return String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function fmt(n) {
    try { return Number(n).toLocaleString('es-MX'); } catch (e) { return String(n); }
  }
  function lev(a, b) {
    if (a === b) return 0;
    var m = a.length, n = b.length, i, j, prev = [], cur;
    if (!m) return n;
    if (!n) return m;
    for (j = 0; j <= n; j++) prev[j] = j;
    for (i = 1; i <= m; i++) {
      cur = [i];
      for (j = 1; j <= n; j++) {
        cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1));
      }
      prev = cur;
    }
    return prev[n];
  }
  function wordStarts(name, q) {
    var w = name.split(' ');
    for (var i = 0; i < w.length; i++) if (w[i].indexOf(q) === 0) return true;
    return false;
  }
  function handleOf(url) {
    var m = /\/collections\/([^/?#]+)/.exec(url || '');
    return m ? m[1] : '';
  }
  function absImg(u) { return !u ? '' : (u.indexOf('//') === 0 ? 'https:' + u : u); }

  /* ───────── carga del registro de marcas ───────── */
  function prepare(list) {
    var byHandle = {}, subHandles = {};
    list.forEach(function (b) {
      b._names = [b.n].concat(b.a || []).map(norm).filter(Boolean);
      b._comp = b._names.map(function (n) { return n.replace(/ /g, ''); });
      byHandle[handleOf(b.h)] = true;
      (b.s || []).forEach(function (s) { s._n = norm(s.l); subHandles[handleOf(s.h)] = true; });
    });
    list._main = byHandle;
    list._subs = subHandles;
    return list;
  }
  function readCache() {
    try {
      var o = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
      if (o && o.d && o.d.length && Date.now() - o.t < CACHE_TTL) return o;
    } catch (e) { /* sin almacenamiento */ }
    return null;
  }
  function fetchBrands() {
    return fetch(SECTION_URL, { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
      .then(function (html) {
        var m = /<script[^>]*id="jinsa-brands-json"[^>]*>([\s\S]*?)<\/script>/.exec(html);
        if (!m) throw new Error('sin datos');
        var data = JSON.parse(m[1]);
        try { localStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), d: data })); } catch (e) { /* ok */ }
        return data;
      });
  }
  function load() {
    if (brands) return Promise.resolve(brands);
    if (loading) return loading;
    var c = readCache();
    if (c) { brands = prepare(c.d); return Promise.resolve(brands); }
    loading = fetchBrands().then(function (d) { brands = prepare(d); return brands; }).catch(function () { loading = null; return null; });
    return loading;
  }

  /* ───────── coincidencias ───────── */
  function tierFor(b, qn, qc) {
    var best = 9, i, n, c, t;
    for (i = 0; i < b._names.length; i++) {
      n = b._names[i]; c = b._comp[i]; t = 9;
      if (n === qn || c === qc) t = 0;
      else if (n.indexOf(qn) === 0 || c.indexOf(qc) === 0) t = 1;
      else if (qn.indexOf(' ') < 0 && i === 0 && wordStarts(n, qn)) t = 2;
      else if (qc.length >= 3 && c.indexOf(qc) >= 0) t = 3;
      else if (qc.length >= 5 && (lev(qc, c.slice(0, qc.length)) <= 1 || lev(qc, c) <= 1)) t = 4;
      if (t < best) best = t;
    }
    return best;
  }
  function matchBrands(q) {
    var qn = norm(q), qc = qn.replace(/ /g, '');
    if (!list_ok() || qc.length < MIN_CHARS) return { cards: [], subs: [], qn: qn };
    var scored = [];
    brands.forEach(function (b) {
      var t = tierFor(b, qn, qc);
      if (t > 4 && qn.indexOf(' ') > -1) {
        // varias palabras ("sensor autonics"): basta que una palabra coincida con una marca
        var tokens = qn.split(' ');
        for (var i = 0; i < tokens.length; i++) {
          if (tokens[i].length >= 3 && tierFor(b, tokens[i], tokens[i]) <= 1) { t = 2; break; }
        }
      }
      if (t <= 4) scored.push({ b: b, t: t });
    });
    scored.sort(function (x, y) { return x.t - y.t || y.b.c - x.b.c || x.b.n.localeCompare(y.b.n); });
    var strong = scored.filter(function (x) { return x.t <= 2; });
    var pool = strong.length ? strong : scored.filter(function (x) { return x.t <= 3; });
    if (!pool.length) pool = scored;
    var cards = pool.slice(0, MAX_CARDS);

    // subcolecciones que coinciden con lo escrito ("encoders" → Autonics · Encoders)
    var subs = [];
    if (qc.length >= 3) {
      var shown = {};
      cards.forEach(function (x) { shown[x.b.h] = true; });
      brands.forEach(function (b) {
        if (shown[b.h]) return;
        (b.s || []).forEach(function (s) {
          if (s._n === qn || s._n.indexOf(qn) === 0 || wordStarts(s._n, qn)) subs.push({ b: b, s: s });
        });
      });
      subs.sort(function (x, y) { return y.s.c - x.s.c; });
      subs = subs.slice(0, 2);
    }
    // un acierto aproximado (error de dedo) no se mezcla con coincidencias reales de subcolecciones
    if (subs.length) cards = cards.filter(function (x) { return x.t <= 3; });
    return { cards: cards, subs: subs, qn: qn };
  }
  function list_ok() { return brands && brands.length; }

  /* ───────── HTML ───────── */
  function highlight(name, qn) {
    var plain = name.toLowerCase();
    if (qn && plain.indexOf(qn) === 0) return '<mark>' + esc(name.slice(0, qn.length)) + '</mark>' + esc(name.slice(qn.length));
    return esc(name);
  }
  // Miniatura = imagen de portada de la colección (tarjetas cuadradas de cada marca). Las marcas sin imagen
  // llevan sus iniciales sobre fondo naranja. Para volver a usar solo iniciales, poner USE_LOGOS en false.
  var USE_LOGOS = true;
  function initials(name) {
    var w = String(name || '').split(/[^A-Za-z0-9ÁÉÍÓÚÜÑáéíóúüñ]+/).filter(Boolean);
    if (w.length > 1) return (w[0].charAt(0) + w[1].charAt(0)).toUpperCase();
    return (w[0] || '?').slice(0, 2).toUpperCase();
  }
  function logo(b) {
    var src = USE_LOGOS ? absImg(b.i) : '';
    if (src) return '<span class="jbs__logo"><img src="' + esc(src) + '" alt="" width="56" height="56" decoding="async"></span>';
    return '<span class="jbs__logo jbs__logo--mono" aria-hidden="true">' + esc(initials(b.n)) + '</span>';
  }
  var ARROW = '<svg class="jbs__arrow" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M5 12h14M12 5l7 7-7 7"/></svg>';

  function blockHtml(q) {
    var r = matchBrands(q);
    if (!r.cards.length && !r.subs.length) return '';
    var id = 0, html = '<div class="jbs" data-jbs><p class="jbs__title">' + (r.cards.length ? 'Marcas' : 'Colecciones') + '</p><ul class="jbs__list" role="listbox" aria-label="Marcas">';
    r.cards.forEach(function (x, idx) {
      var b = x.b, chips = '';
      if (idx === 0 && x.t <= 1 && b.s && b.s.length) {
        chips = '<div class="jbs__chips" role="group" aria-label="Categorías de ' + esc(b.n) + '">' + b.s.slice(0, MAX_CHIPS).map(function (s) {
          return '<a class="jbs__chip" href="' + esc(s.h) + '">' + esc(s.l) + '</a>';
        }).join('') + '</div>';
      }
      html += '<li id="jbs-opt-' + (id++) + '" class="jbs__item" role="option">' +
        '<a class="jbs__card" href="' + esc(b.h) + '">' + logo(b) +
        '<span class="jbs__txt"><span class="jbs__name">' + highlight(b.n, r.qn) + '</span>' +
        '<span class="jbs__count">' + fmt(b.c) + ' productos</span></span>' + ARROW + '</a>' + chips + '</li>';
    });
    r.subs.forEach(function (x) {
      html += '<li id="jbs-opt-' + (id++) + '" class="jbs__item jbs__item--sub" role="option">' +
        '<a class="jbs__card" href="' + esc(x.s.h) + '">' + logo(x.b) +
        '<span class="jbs__txt"><span class="jbs__name">' + esc(x.s.l) + '</span>' +
        '<span class="jbs__count">' + esc(x.b.n) + ' · ' + fmt(x.s.c) + ' productos</span></span>' + ARROW + '</a></li>';
    });
    return html + '</ul></div>';
  }

  /* ───────── integración con <predictive-search> ───────── */
  function resultsRoot(inst) {
    return inst.predictiveSearchResults && inst.predictiveSearchResults.querySelector('#predictive-search-results');
  }
  function placeBlock(inst, q) {
    var host = inst.predictiveSearchResults;
    if (!host) return false;
    var old = host.querySelector('[data-jbs]');
    if (old) old.remove();
    if (!list_ok()) return false;
    var html = blockHtml(q);
    if (!html) return false;
    var root = resultsRoot(inst);
    if (root) {
      var heading = root.querySelector('.predictive-search__heading');
      if (heading) heading.insertAdjacentHTML('afterend', html); else root.insertAdjacentHTML('afterbegin', html);
    } else {
      host.innerHTML = '<div id="predictive-search-results" class="jbs-quick">' + html + '<p class="jbs__wait">Buscando productos…</p></div>';
    }
    return true;
  }
  function rankCollections(inst) {
    var ul = inst.querySelector('#predictive-search-collection-list');
    if (!ul || !list_ok()) return;
    var items = Array.prototype.slice.call(ul.children);
    var scored = items.map(function (li, i) {
      var a = li.querySelector('a'), h = handleOf(a && a.getAttribute('href'));
      var g = brands._main[h] ? 0 : brands._subs[h] ? 2 : 1;
      return { li: li, g: g, i: i };
    });
    scored.sort(function (x, y) { return x.g - y.g || x.i - y.i; });
    scored.forEach(function (x) { ul.appendChild(x.li); });
  }
  function decorate(inst) {
    var q = inst.getQuery();
    if (!list_ok() || q.length < MIN_CHARS) return;
    placeBlock(inst, q);
    rankCollections(inst);
  }
  function onInstantInput(inst) {
    var q = inst.getQuery();
    if (q.length < MIN_CHARS) {                          // con 0-1 letras no hay marcas que mostrar
      var stale = inst.predictiveSearchResults && inst.predictiveSearchResults.querySelector('[data-jbs]');
      if (stale) stale.remove();
      return;
    }
    load().then(function () {
      if (inst.getQuery() !== q) return;                 // el cliente siguió escribiendo
      var had = !!resultsRoot(inst);
      var placed = placeBlock(inst, q);
      if (placed && !had) {
        inst.setAttribute('results', true);
        inst.open();
      }
    });
  }
  function attach(inst) {
    if (inst.__jbsReady || !inst.input) return;
    inst.__jbsReady = true;
    inst.input.addEventListener('input', function () { onInstantInput(inst); });
    inst.input.addEventListener('focus', function () { load(); }, { once: true });
  }

  function patch(C) {
    var P = C.prototype;
    if (P.__jbsPatched) return;
    P.__jbsPatched = true;

    // La predicción arranca desde 2 caracteres, sin importar el ajuste del tema.
    P.getMinQueryLength = function () { return MIN_CHARS; };

    var origRender = P.renderSearchResults;
    P.renderSearchResults = function (markup) {
      origRender.call(this, markup);
      try { decorate(this); } catch (e) { /* si algo falla, quedan los resultados normales */ }
    };

    Array.prototype.forEach.call(document.querySelectorAll('predictive-search'), attach);
    var origSetup = P.setupEventListeners;
    P.setupEventListeners = function () { origSetup.call(this); attach(this); };
  }

  function boot() {
    customElements.whenDefined('predictive-search').then(function () {
      patch(customElements.get('predictive-search'));
      // precarga tranquila para que la primera búsqueda ya tenga las marcas
      (window.requestIdleCallback || function (f) { setTimeout(f, 1500); })(function () { load(); });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
